import { authApi } from 'services/Auth'

/**
 * Marker for Drupal's private file endpoint.
 *
 * Public files are served straight by the webserver and carry no CORS headers,
 * so they can only be opened by navigation. Private files go through Drupal,
 * which requires an OAuth bearer token but does send CORS headers. The two are
 * mutually exclusive, so the URL shape decides how to fetch it — which keeps
 * this working regardless of whether the app or the backend deploys first.
 */
const PRIVATE_FILE_PATH = '/system/files/'

export const needsAuthenticatedDownload = (url?: string): boolean => !!url && url.includes(PRIVATE_FILE_PATH)

/**
 * Hands a blob to the browser as a download named `fileName`.
 *
 * A blob URL has no filename of its own, so the download attribute on a
 * temporary anchor is what gives the saved file a sensible name. Drupal's
 * private file response sets no Content-Disposition, and the CORS config does
 * not expose response headers to JavaScript, so the name has to come from the
 * GraphQL MediaFile.name field rather than from the response.
 */
export const saveBlob = (blob: Blob, fileName?: string): void => {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = fileName || 'document'
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  // Give the browser a moment to start the download before revoking the URL.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
}

/**
 * Ends the session and sends the user to sign in again.
 *
 * Access tokens are short-lived. When one expires and the refresh fails, every
 * subsequent request is rejected — so the honest response is to ask for a new
 * sign-in, not to report a status code the person cannot act on.
 */
const reauthenticate = async (): Promise<void> => {
  await authApi.logout()

  if (!window.location.pathname.startsWith('/login')) {
    window.location.assign('/login')
  }
}

/**
 * Downloads a document, authenticating when the URL points at private storage.
 *
 * Professional documents (VOG, CV, VCI, KOR and extra documents) live in
 * Drupal's private file storage, so an unauthenticated request is rejected with
 * a 403. Public URLs are opened by navigation as before.
 *
 * Resolves to true when the download started. An expired session resolves to
 * false after redirecting to the login page; anything else throws a message
 * that is safe to show to the person using the app.
 */
export const downloadDocument = async (url?: string, fileName?: string): Promise<boolean> => {
  if (!url) {
    return false
  }

  if (!needsAuthenticatedDownload(url)) {
    window.open(url, '_blank', 'noopener')
    return true
  }

  const accessToken = await authApi.getAccessToken()

  // An empty token means the refresh already failed. Sending the request anyway
  // guarantees a 403 and reports it as a download problem, which is misleading:
  // the document is fine, the session is not.
  if (!accessToken) {
    await reauthenticate()
    return false
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (response.status === 401 || response.status === 403) {
    await reauthenticate()
    return false
  }

  if (!response.ok) {
    throw new Error('Het document kon niet worden gedownload. Probeer het opnieuw.')
  }

  // Using blob() rather than a hardcoded type keeps the Content-Type the
  // server sent: these documents can be pdf, jpeg, png, txt, doc or docx.
  saveBlob(await response.blob(), fileName)

  return true
}
